// 本地英文检查：Harper（WebAssembly，在浏览器的 Worker 里跑，不调模型、不上传文字）。
// 第一次用到才加载（约 15 MB，压缩后小得多）；加载不了就当没有检查，不影响写作。沿用旧灵智原生 daily-grammar.ts 的中文说明。

export interface LocalIssue {
  from: number
  to: number
  /** 出问题的那段原文（应用替换前核对一下还对得上）。 */
  text: string
  kind: string
  message: string
  replacements: string[]
}

type Linter = import('harper.js').WorkerLinter
let linter: Promise<Linter | null> | undefined

function getLinter(): Promise<Linter | null> {
  linter ??= Promise.all([import('harper.js'), import('harper.js/binary')])
    .then(async ([harper, { binary }]) => {
      const l = new harper.WorkerLinter({ binary, dialect: harper.Dialect.American })
      await l.setup()
      return l
    })
    .catch((e) => {
      console.warn('本地英文检查加载失败', e)
      return null
    })
  return linter
}

const LABELS: Record<string, string> = {
  Agreement: '主谓或词形搭配需要检查',
  BoundaryError: '这里的词好像连在一起或断开了',
  Capitalization: '大小写可能不合适',
  Eggcorn: '这个说法可能是误用',
  Formatting: '格式可以更一致',
  Grammar: '这里可能有语法问题',
  Malapropism: '这个词可能用错了',
  Punctuation: '标点可能需要调整',
  Readability: '这句话可以更清楚',
  Redundancy: '这里可能有重复表达',
  Repetition: '相近表达出现得较频繁',
  Spelling: '这个单词可能拼写有误',
  Style: '这个表达可以更自然',
  Typo: '这里可能有输入错误',
  Usage: '这个词的用法需要检查',
  WordChoice: '可以考虑更准确的用词',
  WordOrder: '语序可能需要调整',
}

export const issueLabel = (kind: string) => LABELS[kind] ?? '这处英文值得检查'

/** Harper 的位置按字符（Unicode 码点）数；JS 字符串按 UTF-16 数，有 emoji 时要换算。 */
export function utf16Index(text: string, charIndex: number): number {
  if (!/[\uD800-\uDFFF]/.test(text)) return charIndex
  let i = 0
  let chars = 0
  while (i < text.length && chars < charIndex) {
    const code = text.charCodeAt(i)
    i += code >= 0xd800 && code <= 0xdbff ? 2 : 1
    chars++
  }
  return i
}

export async function checkEnglish(text: string): Promise<LocalIssue[]> {
  if (!text.trim() || typeof Worker === 'undefined') return []
  const l = await getLinter()
  if (!l) return []
  const lints = await l.lint(text, { language: 'markdown', isolateEnglish: true, dedup: true })
  return lints.flatMap((lint) => {
    try {
      const span = lint.span()
      const from = Math.min(utf16Index(text, span.start), text.length)
      const to = Math.max(from, Math.min(utf16Index(text, span.end), text.length))
      span.free()
      const suggestions = lint.suggestions()
      const replacements = suggestions.map((s) => s.get_replacement_text()).slice(0, 3)
      suggestions.forEach((s) => s.free())
      const kind = lint.lint_kind()
      return [{ from, to, text: text.slice(from, to), kind, message: lint.message(), replacements }]
    } finally {
      lint.free()
    }
  })
}
